import type { BuiltInDepartment } from '$lib/domain/departments';
import type { BuiltInPlace } from '$lib/domain/places';
import type { ThemeId } from '$lib/state/theme.svelte';

/** "Sofie", "Sofie og Jonas", "Sofie, Jonas og Mia". */
function andList(items: readonly string[]): string {
	return items.length < 2
		? (items[0] ?? '')
		: `${items.slice(0, -1).join(', ')} og ${items.at(-1)}`;
}

/** "Jonas", "Jonas eller Sofie", "Jonas, Sofie eller Mia". */
function orList(items: readonly string[]): string {
	return items.length < 2
		? (items[0] ?? '')
		: `${items.slice(0, -1).join(', ')} eller ${items.at(-1)}`;
}

/** "Sofies", "Jonas'". */
function possessive(name: string): string {
	return /[sxz]$/i.test(name) ? `${name}'` : `${name}s`;
}

/**
 * Danish UI copy — the source catalogue, verbatim from the design handoff.
 * Messages with values are functions, so each language owns its word order and plural rules.
 */
/** The same answer wherever a unit taken out of use (or not in the catalogue) is written. */
const unknownUnit = 'Den enhed bruges ikke længere. Vælg en anden.';

export const da = {
	app: {
		name: 'Daglio',
		/** The wordmark next to the logo. */
		brand: 'Daglio'
	},

	common: {
		all: 'Alle',
		add: 'Tilføj',
		cancel: 'Annullér',
		/** Backs out of an inline confirm. */
		cancelConfirm: 'Fortryd',
		close: 'Luk',
		edit: 'Redigér',
		remove: 'Fjern',
		reset: 'Nulstil',
		saveChanges: 'Gem ændringer',
		saving: 'Gemmer…',
		saved: 'Gemt',
		retry: 'Prøv igen',
		loading: 'Henter…',
		today: 'I dag',
		amount: 'Antal',
		item: 'Vare',
		unit: 'Enhed',
		/** A stored unit no longer offered, shown in a unit picker so the value is kept: "kop (udgået)". */
		retiredUnit: (unit: string) => `${unit} (udgået)`,
		department: 'Afdeling',
		noDate: 'Ingen dato',
		check: 'Afkryds',
		fewer: 'Færre',
		more: 'Flere',
		copied: '✓ Kopieret',
		copyCode: 'Kopiér kode',
		inviteCode: 'Invitationskode',
		copyMondayToWeekdays: 'Kopiér mandag til hverdage',
		/** The drag handle of a row in an ordered list (hold and drag, or ↑/↓). */
		dragHandle: (name: string) => `Flyt ${name}`,
		/** Read out after a move with the arrow keys or a drag. */
		movedTo: (name: string, position: number, count: number) =>
			`${name} er nu nr. ${position} af ${count}`,
		noMatchingRecipes: 'Ingen retter matcher.',
		servings: (count: number) => `${count} pers.`,
		inPantry: (quantity: string) => `Har ${quantity} hjemme`,
		suggestions: 'Forslag',
		didYouMean: 'Mente du …?',
		itemCount: (count: number) => `${count} varer`,
		shareInvite: (household: string, code: string) =>
			`Deltag i ${household} på Daglio med koden ${code}`
	},

	errors: {
		unitsUnavailable: 'Enhederne kunne ikke hentes. Træk ned for at prøve igen.',
		offline: 'Du er offline – prøv igen, når du har forbindelse.',
		generic: 'Noget gik galt. Prøv igen.',
		/** API error codes on the auth screens and household settings. */
		auth: {
			unknown_email: 'Vi kender ikke den email. Opret en konto i stedet.',
			wrong_password: 'Forkert adgangskode. Prøv igen.',
			email_taken: 'Der findes allerede en konto med den email.',
			name_required: 'Skriv dit navn.',
			invalid_code_format: 'Koden er 6 tegn, fx K7Q-4MX.',
			unknown_code: 'Vi kender ikke den kode. Tjek den og prøv igen.',
			already_in_household: 'Du er allerede med i en husstand.',
			already_member: 'Personen er allerede med.',
			invalid_reset_token: 'Linket er udløbet eller allerede brugt. Bed om et nyt.',
			email_failed: 'Mailen kunne ikke sendes. Prøv igen senere.'
		},
		chores: {
			name_required: 'Skriv et opgavenavn på højst 100 tegn.',
			invalid_assignee: 'Vælg en person fra husstanden eller Alle.',
			invalid_recurrence: 'Vælg et positivt heltal og en slutdato på eller efter startdatoen.',
			recurrence_start_before_change: 'Den nye startdato skal være på eller efter ændringsdatoen.',
			chore_change_in_past:
				'Gentagne opgaver kan kun oprettes eller ændres fra i dag eller senere.',
			chore_version_changed:
				'Denne version er ændret, eller datoen tilhører en anden version. Opdatér listen og prøv igen.',
			invalid_occurrence: 'Opgaven gentages ikke på denne dato. Opdatér listen og prøv igen.'
		},
		apiTokens: {
			name_required: 'Giv tokenet et navn.'
		},
		sharing: {
			admin_required: 'Kun husstandens admins kan ændre, hvad der deles.',
			not_a_copy: 'Der er ingen fælles version at gå tilbage til.'
		},
		related: {
			same_recipe: 'Vælg en anden ret.',
			shared_recipe: 'Retten deles af en anden husstand, så dens relationer kan kun ændres der.'
		},
		/** The OAuth consent page. */
		oauth: {
			invalid_client: 'Appen er ikke registreret. Start forbindelsen forfra fra appen.',
			invalid_redirect_uri:
				'Appen bad om at vende tilbage til en adresse, den ikke har registreret.',
			invalid_request: 'Linket er ufuldstændigt. Start forbindelsen forfra fra appen.'
		},
		recipeImport: {
			invalid_url: 'Linket ser ikke rigtigt ud.',
			unsupported_site: 'Vi kan ikke hente ingredienser fra den side endnu.',
			fetch_failed: 'Siden kunne ikke hentes. Prøv igen senere.',
			no_ingredients: 'Vi fandt ingen ingredienser på siden.'
		},
		admin: {
			super_admin_required: 'Kun platformens administratorer har adgang.',
			item_exists: 'Der findes allerede en vare med det navn i husstanden.',
			name_required: 'Skriv det navn, varerne skal have.',
			nothing_to_merge: 'Vælg mindst ét andet navn end det, der flettes til.',
			same_name: 'Vælg to forskellige navne.',
			email_failed: 'Mailen kunne ikke sendes. Prøv igen senere.',
			cannot_delete_self: 'Du kan ikke slette din egen konto eller husstand.',
			cannot_remove_self: 'Du kan ikke fjerne dig selv fra husstanden.',
			last_household_admin: 'Husstanden skal have mindst én admin.',
			unit_exists: 'Der er allerede en enhed eller et andet navn, der hedder sådan.',
			unit_in_use:
				'Enheden er i brug. Den kan skjules, men ikke slettes eller regnes om på en ny måde.',
			invalid_unit: 'En enhed skal have et navn på højst 10 tegn uden | og +.',
			invalid_unit_conversion:
				'Den omregning går ikke: samme mål kan kun være en enhed uden egen omregning, omregninger må ikke gå i ring, og en enhed, der tælles i hele, kan ikke være samme mål som en anden.',
			system_unit: 'stk og port. bruges af appen; de kan kun flyttes og få andre navne.',
			fractional_base_conversion:
				'Den omregning går ikke: "Samme mål" kræver en hel mængde af grundenheden.',
			unknown_unit: unknownUnit
		},
		recipes: {
			unknown_unit: unknownUnit
		},
		shopping: {
			nothing_in_pantry: 'Der er ikke mere af varen på lager.',
			line_already_moved: 'Varen er allerede flyttet.',
			unknown_unit: unknownUnit
		},
		stores: {
			store_not_found: 'Butikken findes ikke længere.',
			store_name_required: 'Giv butikken et navn.',
			store_name_taken: 'I har allerede en butik med det navn.',
			department_name_required: 'Giv afdelingen et navn.',
			department_name_taken: 'I har allerede en afdeling med det navn.',
			feature_disabled: 'Butikker er ikke slået til for jeres husstand.',
			unknown_unit: unknownUnit
		},
		staples: {
			amount_required: 'Skriv hvor meget I vil have på lager.',
			feature_disabled: 'Basisvarer med interval er ikke slået til for jeres husstand.',
			unknown_unit: unknownUnit
		},
		wishes: {
			wish_required: 'Vælg en ret eller skriv, hvad I har lyst til.',
			recipe_required: 'Vælg den ret, ønsket skal være.',
			recipe_not_found: 'Retten findes ikke længere.',
			invalid_date: 'Ønsker kan kun sættes på næste uge.',
			wish_already_placed: 'Ønsket er allerede sat på planen.',
			try_again: 'Ønskerne blev ændret imens. Prøv igen.',
			not_found: 'Ønsket findes ikke længere.',
			feature_disabled: 'Ønsker er ikke slået til for husstanden.'
		},
		inspiration: {
			too_few_days: 'En uge kan deles, når mindst 3 dage er planlagt.',
			nothing_to_share: 'Der er ingen retter at dele.',
			sharing_disabled: 'Kun husstandens admin kan dele til Inspiration.',
			title_required: 'Giv delingen et navn.',
			slot_required: 'Vælg måltidet, der skal deles.',
			nothing_to_place: 'Der er ingen tomme dage at lægge ind på.',
			invalid_day: 'Vælg en dag fra det delte.',
			use_undone: 'Det er allerede fortrudt.',
			try_again: 'Planen blev ændret imens. Prøv igen.',
			not_found: 'Delingen findes ikke længere.',
			feature_disabled: 'Inspiration er ikke slået til for husstanden.',
			own_share: 'I kan ikke anmelde jeres egen deling.',
			report_resolved: 'Anmeldelsen er allerede behandlet.'
		},
		daySwaps: {
			invalid_days: 'Vælg en anden dag i samme uge.',
			day_passed: 'Dagen er allerede gået.',
			plan_changed: 'Planen er ændret imens.',
			day_has_proposal: 'Der er allerede et forslag til en af dagene.',
			own_proposal: 'Du kan ikke selv svare på dit forslag.',
			not_proposer: 'Kun den, der foreslog byttet, kan trække det tilbage.',
			not_rejecter: 'Kun den, der afviste, kan fortryde det.',
			proposal_closed: 'Forslaget er allerede besvaret.',
			undo_expired: 'Det kan ikke fortrydes længere.',
			feature_disabled: 'Bytte af dage er ikke slået til for husstanden.'
		},
		suggestions: {
			invalid_servings: 'Vælg mellem 1 og 50 personer.'
		},
		plan: {
			nothing_in_pantry: 'Retten er ikke på lager længere.',
			recipe_not_found: 'Retten findes ikke længere.',
			meal_passed: 'Måltidet er allerede spist.',
			not_favorite: 'Retten er ikke din livret længere.',
			try_again: 'Planen blev ændret imens. Prøv igen.'
		},
		pantry: {
			recipe_not_found: 'Retten findes ikke længere.',
			unknown_place: 'Det sted er slået fra. Vælg et andet.',
			unknown_unit: unknownUnit
		},
		meals: {
			duplicate_meal_slot: 'I har allerede et måltid med det navn.',
			meal_slot_too_long: 'Navnet er for langt.',
			unknown_meal_slot: 'Tilføj måltidet til jeres måltider først.'
		},
		setup: {
			setup_completed: 'Husstanden er allerede sat op.',
			amount_required: 'Skriv hvor meget I vil have på lager.',
			name_required: 'Giv alle basisvarer et navn.',
			admin_required: 'Kun husstandens admins kan sætte den op.',
			unknown_unit: unknownUnit
		},
		places: {
			place_in_use: 'Der står stadig varer det sted. Flyt eller brug dem først.',
			place_name_taken: 'I har allerede et sted med det navn.',
			place_name_required: 'Giv stedet et navn.',
			place_required: 'Vælg mindst ét sted.',
			unknown_place: 'Stedet findes ikke længere.'
		},
		barcode: {
			invalid_barcode: 'Det ligner ikke en stregkode.',
			feature_disabled: 'Stregkodescanning er ikke slået til for husstanden.',
			lookup_failed: 'Varen kunne ikke slås op lige nu – skriv den selv.'
		},
		register: {
			item_exists: 'Varen findes allerede.',
			item_in_use: 'Varen bruges stadig og kan ikke slettes.',
			same_unit: 'Begge sider er samme slags enhed, fx kg og g.',
			conflicting_conversion: 'Enhederne kan allerede omregnes. Fjern den gamle omregning først.',
			invalid_amount: 'Mængden skal være større end 0.',
			unknown_unit: unknownUnit
		}
	},

	number: {
		decimalSeparator: ','
	},

	dates: {
		weekdays: ['Mandag', 'Tirsdag', 'Onsdag', 'Torsdag', 'Fredag', 'Lørdag', 'Søndag'],
		weekdaysShort: ['Man', 'Tir', 'Ons', 'Tor', 'Fre', 'Lør', 'Søn'],
		/** Weekdays inside a sentence: "Byttet: tirsdag ⇄ fredag". */
		weekdaysInText: ['mandag', 'tirsdag', 'onsdag', 'torsdag', 'fredag', 'lørdag', 'søndag'],
		months: ['jan', 'feb', 'mar', 'apr', 'maj', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'],
		weekdaysAbbr: ['man.', 'tir.', 'ons.', 'tor.', 'fre.', 'lør.', 'søn.'],
		monthsAbbr: [
			'jan.',
			'feb.',
			'mar.',
			'apr.',
			'maj',
			'jun.',
			'jul.',
			'aug.',
			'sep.',
			'okt.',
			'nov.',
			'dec.'
		],
		monthsLong: [
			'januar',
			'februar',
			'marts',
			'april',
			'maj',
			'juni',
			'juli',
			'august',
			'september',
			'oktober',
			'november',
			'december'
		],
		/** "tor. 30. okt.", "30. okt." or "tor. 30." */
		shortDate: (weekday: string | null, day: number, month: string | null) =>
			[weekday, `${day}.`, month].filter(Boolean).join(' '),
		/** "22. sep" */
		dayMonth: (day: number, month: string) => `${day}. ${month}`,
		/** "Mandag 22. sep" */
		longDay: (weekday: string, dayMonth: string) => `${weekday} ${dayMonth}`,
		/** "Uge 39 · 22. sep – 28. sep" */
		weekLabel: (week: number, from: string, to: string) => `Uge ${week} · ${from} – ${to}`,
		thisWeek: 'Denne uge',
		nextWeek: 'Næste uge',
		lastWeek: 'Sidste uge',
		/** This week's Madplan and Opgaver hide the days that have passed until shown. */
		showPastDays: (count: number) =>
			count === 1 ? 'Vis 1 tidligere dag' : `Vis ${count} tidligere dage`,
		hidePastDays: 'Skjul tidligere dage',
		inWeeks: (count: number) => `Om ${count} uger`,
		today: 'I dag',
		yesterday: 'I går',
		daysAgo: (count: number) => `${count} dage siden`,
		weeksAgo: (count: number) => (count === 1 ? '1 uge siden' : `${count} uger siden`),
		monthsAgo: (count: number) => (count === 1 ? '1 måned siden' : `${count} måneder siden`),
		overAYearAgo: 'Over et år siden',
		addedToday: 'Lagt ind i dag',
		addedYesterday: 'Lagt ind i går',
		addedDaysAgo: (count: number) => `Lagt ind for ${count} dage siden`,
		addedWeeksAgo: (count: number) => `Lagt ind for ${count} uger siden`,
		addedMonthsAgo: (count: number) => `Lagt ind for ${count} mdr. siden`,
		/** Durations for shelf-life pickers: "3 dage", "1 uge", "1 md.", "2 år". */
		duration: {
			days: (count: number) => (count === 1 ? '1 dag' : `${count} dage`),
			weeks: (count: number) => (count === 1 ? '1 uge' : `${count} uger`),
			months: (count: number) => (count === 1 ? '1 md.' : `${count} mdr.`),
			years: (count: number) => `${count} år`
		}
	},

	departments: {
		gron: 'Frugt & grønt',
		kod: 'Kød & fisk',
		mejeri: 'Mejeri & køl',
		brod: 'Brød & bageri',
		kolonial: 'Kolonial',
		frost: 'Frost',
		andet: 'Andet'
	} satisfies Record<BuiltInDepartment, string>,

	places: {
		fryser: 'Fryser',
		koel: 'Køleskab',
		skab: 'Skabe',
		viktualierum: 'Viktualierum'
	} satisfies Record<BuiltInPlace, string>,
	/** "har 5 dl i køleskabet" */
	placeIn: {
		fryser: 'i fryseren',
		koel: 'i køleskabet',
		skab: 'i skabene',
		viktualierum: 'i viktualierummet'
	} satisfies Record<BuiltInPlace, string>,
	/** "1 port. fra fryseren" */
	placeFrom: {
		fryser: 'fra fryseren',
		koel: 'fra køleskabet',
		skab: 'fra skabene',
		viktualierum: 'fra viktualierummet'
	} satisfies Record<BuiltInPlace, string>,
	/** A household's own place, e.g. "i Kælder". */
	ownPlaceIn: (name: string) => `i ${name}`,
	ownPlaceFrom: (name: string) => `fra ${name}`,

	/** "Kom i gang": the setup flow right after creating a household (design: Oprettelsesflow). */
	setup: {
		pageTitle: 'Kom i gang',
		skip: 'Spring over',
		back: '‹ Tilbage',
		next: 'Næste',
		/** "⚙ Kan ændres når som helst under Indstillinger → Ugeskabelon" */
		changeLater: 'Kan ændres når som helst under',
		stepKicker: (step: number, name: string) => `Trin ${step} af 4 · ${name}`,
		mealsPerWeek: (count: number) => `${count} måltider om ugen`,
		intro: {
			kicker: 'Velkommen til Daglio',
			title: (name: string) => `Hej ${name} – jeres husstand er klar`,
			text: 'Vi har lavet et udgangspunkt, så I kan gå i gang med det samme. Lad os kigge det hurtigt igennem, så det passer til jer.',
			cardTitle: 'Det har vi sat op for jer',
			plan: 'Aftensmad man–fre og tre måltider i weekenden',
			shop: (count: number) => `Fyldes ud fra madplanen + ${count} basisvarer`,
			tasks: (count: number) => `${count} faste opgaver fordelt over ugen`,
			pantry: 'Fryser, køleskab og skabe',
			takesAMinute: 'Tager ca. 1 minut. I kan rette alt undervejs.',
			footNote: 'Tager ca. 1 minut',
			submit: 'Kom i gang'
		},
		plan: {
			name: 'Madplan',
			title: 'Hvilke måltider planlægger I?',
			text: 'Skabelonen bestemmer, hvilke tomme pladser der står klar på hver dag, når I planlægger en ny uge.',
			points: [
				'Tryk på et måltid for at slå det til eller fra.',
				'I kan altid tilføje et ekstra måltid på en enkelt dag.'
			],
			where: 'Indstillinger → Ugeskabelon',
			cardTitle: 'Ugeskabelon',
			presets: {
				dinner: 'Kun aftensmad',
				weekdaysWeekend: 'Hverdag + weekend',
				all: 'Alle måltider'
			},
			exampleSlot: 'Aften · mandag',
			exampleMeal: 'Vælg ret',
			exampleNote: 'Sådan ser en tom plads ud'
		},
		shop: {
			name: 'Indkøb',
			title: 'Indkøbslisten skriver sig selv',
			text: 'Når I vælger retter i madplanen, kommer ingredienserne automatisk på listen – lagt sammen og sorteret efter afdeling i butikken.',
			points: [
				'Basisvarer er det, I køber hver uge uanset madplanen.',
				'Det I køber, kan flyttes direkte over i beholdningen.'
			],
			where: 'Indstillinger → Basisvarer',
			cardTitle: 'Listen kommer tre steder fra',
			sources: [
				{ name: 'Fra madplanen', text: 'Ingredienser fra ugens retter' },
				{ name: 'Basisvarer', text: 'Det I altid skal bruge' },
				{ name: 'Manuelt', text: 'Alt det andet' }
			],
			yourStaples: 'Jeres basisvarer',
			selected: (count: number) => `${count} valgt`,
			select: (name: string) => `Tilføj ${name}`,
			deselect: (name: string) => `Fjern ${name}`,
			amount: 'Antal',
			/** Under a staple typed in a unit the app does not offer. */
			unknownUnit: (units: string) => `Ukendt enhed. Brug en af: ${units}`,
			remove: 'Slet',
			everyWeek: 'Hver uge',
			keepInStock: '⌂ Hold på lager',
			newStaple: 'Tilføj basisvare, fx Havregryn',
			add: '+ Tilføj',
			everyWeekNote: 'kommer altid på listen.',
			keepInStockNote: 'kommer kun på, når I er ved at løbe tør.',
			footNote: (count: number) => `${count} basisvarer`
		},
		tasks: {
			name: 'Opgaver',
			title: 'Del ansvaret derhjemme',
			text: 'Faste opgaver dukker op hver uge på de dage, I vælger. Hver opgave kan have én ansvarlig – eller være fælles.',
			points: [
				'Slå de opgaver til, I vil bruge.',
				'Tryk på navnet for at skifte, hvem der har den.'
			],
			where: 'Indstillinger → Opgaveskabelon',
			cardTitle: 'Faste opgaver',
			selected: (on: number, total: number) => `${on} af ${total} valgt`,
			everyDay: 'Hver dag',
			switchWho: 'Skift hvem',
			tapToSwitch: 'Tryk for at skifte:',
			footNote: (count: number) => `${count} opgaver`
		},
		pantry: {
			name: 'Beholdning',
			title: 'Hold styr på hvad I har',
			text: 'Beholdningen viser, hvad der står i fryser, køleskab og skabe – og hvornår det skal bruges.',
			points: [
				'Vælg de steder, I har derhjemme.',
				'Når noget er på lager, trækker vi det fra indkøbslisten.'
			],
			where: 'Beholdning',
			howWeTrack: 'Sådan holder vi øje',
			examples: ['Brug i dag', '3 dage tilbage', 'Til 3. dec'],
			useSoonNote: 'Varer der snart udløber, vises øverst under',
			useSoon: 'Brug snart',
			footNote: (count: number) => `${count} steder`,
			submit: 'Færdig'
		},
		done: {
			kicker: 'Alt er sat op',
			title: 'Så er I klar!',
			text: 'Start med at planlægge denne uge. Indkøbslisten fylder sig selv op, efterhånden som I vælger retter.',
			cardTitle: 'Klar til første uge',
			plan: 'Madplan',
			shop: 'Indkøbsliste',
			tasks: 'Opgaver',
			pantry: 'Beholdning',
			weekdayDinners: (weekendMeals: number) =>
				`Aftensmad man–fre · ${weekendMeals} måltider i weekenden`,
			staples: (count: number, inStock: number) =>
				`${count} basisvarer · ${inStock} holdes på lager`,
			chores: (count: number) => `${count} faste opgaver om ugen`,
			noChores: 'Ingen faste opgaver endnu',
			noPlaces: 'Ingen steder valgt',
			edit: 'Ret',
			submit: 'Planlæg første uge'
		}
	},

	/** Choosing the household's storage places (setup step 4 and the Beholdning page). */
	placePicker: {
		title: 'Hvor gemmer I mad?',
		open: 'Steder',
		text: {
			fryser: 'Rester, kød og hele retter',
			koel: 'Mejeri, pålæg og frisk',
			skab: 'Kolonial, dåser og tørvarer',
			viktualierum: 'Kælder, bryggers eller spisekammer'
		} satisfies Record<BuiltInPlace, string>,
		life: {
			fryser: 'Holder typisk 3 mdr.',
			koel: 'Holder typisk 5 dage',
			skab: 'Holder typisk 6 mdr.',
			viktualierum: 'Holder typisk 1 md.'
		} satisfies Record<BuiltInPlace, string>,
		ownText: 'Eget sted',
		ownLife: 'Holdbarhed vælges pr. vare',
		newPlace: 'Eget sted, fx Kælder eller Garage',
		add: '+ Tilføj sted',
		remove: 'Fjern sted',
		save: 'Gem steder'
	},

	themes: {
		solskin: { name: 'Solskin', text: 'Varm gul med kraftige kanter' },
		slik: { name: 'Slik', text: 'Blød pink og mint' },
		aften: { name: 'Aften', text: 'Mørkt tema til aftenen' },
		have: { name: 'Have', text: 'Frisk grøn med orange' },
		hav: { name: 'Hav', text: 'Luftig blå og koral' },
		lakrids: { name: 'Lakrids', text: 'Sort med neon-pink og lime' }
	} satisfies Record<ThemeId, { name: string; text: string }>,

	nav: {
		plan: { label: 'Madplan', short: 'Madplan' },
		chores: { label: 'Opgaver', short: 'Opgaver' },
		recipes: { label: 'Retter', short: 'Retter' },
		inspiration: { label: '✦ Inspiration', short: 'Inspiration' },
		shopping: { label: 'Indkøb', short: 'Indkøb' },
		pantry: { label: 'Beholdning', short: 'Lager' },
		admin: { label: 'Admin', short: 'Admin' },
		settings: { label: 'Indstillinger', short: 'Opsætning' }
	},

	/** Screen-reader labels of the loading spinner per state. */
	spinner: {
		loading: 'Indlæser',
		success: 'Fuldført',
		error: 'Fejlede'
	},

	layout: {
		offline: 'Du er offline – ændringer på indkøbslisten gemmes og sendes senere.',
		backOnline: (count: number) =>
			`Du er online igen – ${count} ${count === 1 ? 'afkrydsning' : 'afkrydsninger'} sendt.`,
		newVersion: 'Ny version klar',
		update: 'Opdatér',
		openMenu: 'Åbn menu',
		menu: 'Menu',
		closeMenu: 'Luk menu',
		household: 'Husstand',
		signedInAs: (name: string) => `Logget ind som ${name}`,
		signOut: 'Log ud',
		previousWeek: 'Forrige uge',
		nextWeek: 'Næste uge'
	},

	auth: {
		headline: 'Mad, indkøb og opgaver. Samlet ét sted.',
		lead: 'Planlæg ugen sammen, lad indkøbslisten skrive sig selv, og del ansvaret derhjemme.',
		features: {
			plan: 'Planlæg ugen og genbrug retter',
			shopping: 'Listen skriver sig selv',
			pantry: 'Fryser, køleskab og skabe',
			chores: 'Del ansvaret derhjemme'
		},
		steps: {
			login: {
				title: 'Velkommen tilbage',
				subtitle: 'Log ind for at se jeres madplan, indkøbsliste og opgaver.',
				submit: 'Log ind'
			},
			signup: {
				title: 'Opret konto',
				subtitle: 'Det tager et minut. Bagefter opretter eller deltager du i en husstand.',
				submit: 'Fortsæt'
			},
			household: {
				title: 'Jeres husstand',
				subtitle: 'Madplan, indkøbsliste, beholdning og opgaver deles med alle i husstanden.',
				submit: ''
			},
			code: {
				title: 'Husstanden er klar',
				subtitle: 'Del koden med dem, du bor med. De indtaster den, når de opretter en konto.',
				submit: 'Kom i gang'
			},
			forgot: {
				title: 'Glemt adgangskode?',
				subtitle: 'Skriv din email, så sender vi et link.',
				submit: 'Send link'
			},
			reset: {
				title: 'Ny adgangskode',
				subtitle: 'Vælg en ny adgangskode til din konto.',
				submit: 'Gem adgangskode'
			},
			resetDone: {
				title: 'Klar!',
				subtitle: 'Din adgangskode er ændret. Log ind med den nye.',
				submit: 'Log ind'
			},
			resetInvalid: {
				title: 'Linket virker ikke',
				subtitle: 'Linket er udløbet eller allerede brugt. Bed om et nyt.',
				submit: 'Send nyt link'
			}
		},
		newPassword: 'Ny adgangskode',
		sendLinkAgain: 'Send link igen',
		passwordSaved: '✓ Adgangskoden er gemt',
		linksLastAnHour: 'Links virker i en time.',
		stepOf: (step: number, total: number) => `Trin ${step} af ${total}`,
		createHousehold: 'Opret husstand',
		joinHousehold: 'Deltag i husstand',
		householdModes: {
			create: { label: 'Opret ny', text: 'Start en ny husstand' },
			join: { label: 'Jeg har en kode', text: 'Deltag i en eksisterende' }
		},
		defaultHouseholdName: (name: string) => `${name}s husstand`,
		yourName: 'Dit navn',
		namePlaceholder: 'fx Julie',
		email: 'Email',
		emailPlaceholder: 'navn@mail.dk',
		password: 'Adgangskode',
		passwordPlaceholder: 'Din adgangskode',
		newPasswordPlaceholder: (minLength: number) => `Mindst ${minLength} tegn`,
		showPassword: 'Vis',
		hidePassword: 'Skjul',
		householdName: 'Husstandens navn',
		householdNamePlaceholder: 'fx Julie & Mads',
		inviteOptional: 'Invitér nogen (valgfrit)',
		invitePlaceholder: 'deres@mail.dk',
		inviteCodeFor: (household: string) => `Invitationskode til ${household}`,
		shareCode: 'Del kode',
		alsoSentTo: (email: string) => `Vi har også sendt den til ${email}`,
		fillEmailAndPassword: 'Udfyld email og adgangskode.',
		nameRequired: 'Skriv dit navn.',
		invalidEmail: 'Skriv en gyldig email.',
		passwordTooShort: (minLength: number) => `Adgangskoden skal være mindst ${minLength} tegn.`,
		householdNameRequired: 'Giv husstanden et navn.',
		inviteEmailInvalid: 'Invitationens email ser forkert ud.',
		resetLinkSent: (email: string) => `Vi har sendt et link til ${email}.`,
		forgotPassword: 'Glemt adgangskode?',
		newHere: 'Ny her?',
		createAccount: 'Opret en konto',
		haveAccount: 'Har du allerede en konto?',
		logIn: 'Log ind',
		backSignOut: '‹ Log ud',
		backToLogin: '‹ Tilbage til log ind'
	},

	plan: {
		title: 'Ugens madplan',
		slotsFilled: (filled: number, total: number) => `${filled} af ${total} pladser udfyldt`,
		mealsPlanned: (count: number) => `${count} måltider planlagt`,
		extraMeal: '+ Ekstra måltid',
		pickRecipe: 'Vælg ret',
		openRecipe: 'Åbn ret',
		meal: 'Måltid',
		fewerServings: 'Færre personer',
		moreServings: 'Flere personer',
		removeMeal: 'Fjern måltid',
		mealMenu: 'Mere',
		addMeal: 'Tilføj måltid',
		customName: 'Eget navn',
		servings: 'Antal personer',
		searchRecipe: 'Søg ret',
		searchRecipePlaceholder: 'Søg ret…',
		noRecipes: 'I har ingen retter endnu – opret dem under Retter.',
		inPantry: (servings: number) => `Mad til ${servings} pers. på lager`,
		takeFromPantry: 'Tag fra lager',
		/** "⌂ Der er 8 pers. i fryseren – spis den i stedet →" */
		eatFromPantry: (servings: number, placeIn: string) =>
			`⌂ Der er ${servings} pers. ${placeIn} – spis den i stedet →`,
		/** After the name of a meal eaten from the pantry: "Lasagne · opvarm". */
		reheat: 'opvarm',
		/** "1 port. fra fryseren · intet at købe" */
		takenFromPantry: (portions: string, placeFrom: string) =>
			`${portions} ${placeFrom} · intet at købe`,
		/** In the picker: "⌂ 8 pers. i fryseren" */
		stockIn: (servings: number, placeIn: string) => `⌂ ${servings} pers. ${placeIn}`,
		planIt: 'Planlæg',
		putBack: 'Læg tilbage',
		/** The dish picker's suggestions of favourites that have waited. */
		waitingLivretter: '♥ Livretter der har ventet',
		/** Whose favourite a suggestion is: "Sofies livret", "Din livret". */
		livretOfMember: (name: string) => `${possessive(name)} livret`,
		ownLivret: 'Din livret',
		/** "Sofies livret · ikke lavet i 6 uger" */
		waitedWeeks: (whose: string, weeks: number) =>
			`${whose} · ikke lavet i ${weeks === 1 ? '1 uge' : `${weeks} uger`}`,
		/** A favourite never made yet: "Sofies livret · aldrig lavet". */
		waitedNever: (whose: string) => `${whose} · aldrig lavet`,
		/** "Jonas' livret · ønsket "snart"" */
		wantedSoon: (whose: string) => `${whose} · ønsket "snart"`,
		choose: 'Vælg',
		/** The day after a favourite was made. */
		followUpWhen: 'I går · din livret',
		followUpQuestion: (recipe: string) => `Hvordan var ${recipe}?`,
		makeAgainSoon: 'Lav igen snart',
		pauseForAWhile: 'Pause lidt',
		/** After a meal's label for the members who have it as livret: "Aften · ♥ Sofie". */
		livretLabel: (label: string, names: readonly string[]) => `${label} · ♥ ${andList(names)}`,
		lookingForward: '♥ Glæder mig!',
		/** The planner sees who looks forward to it: "Sofie glæder sig!" */
		othersLookingForward: (names: readonly string[]) => `${andList(names)} glæder sig!`
	},

	/** In-app messages, shown once as a toast. */
	notifications: {
		lookingForward: (name: string, recipe: string) => `${name} glæder sig til ${recipe}!`,
		/** The proposer's message once another member approved: "Jonas godkendte byttet: tirsdag ⇄ fredag". */
		swapApproved: (name: string, first: string, second: string) =>
			`${name} godkendte byttet: ${first} ⇄ ${second}`,
		/** A dish the member wished for was planned: "Pizza er sat på fredag". */
		wishPlaced: (recipe: string, weekday: string) => `${recipe} er sat på ${weekday}`
	},

	/** "Vi har lyst til …": wishes for next week (design: Nye funktioner 7 - Ønsker, 9a). */
	wishes: {
		open: '♡ Vi har lyst til…',
		sheetTitle: 'Vi har lyst til…',
		search: 'Søg en ret eller skriv frit',
		ownRecipe: 'Jeres ret',
		commonRecipe: 'Fælles ret',
		/** "Jeres ret · sidst 3 uger siden" */
		lastMade: (whose: string, ago: string) => `${whose} · sidst ${ago}`,
		/** ""pizz" som fritekst" */
		asText: (text: string) => `"${text}" som fritekst`,
		asTextNote: 'Kan kobles til en ret senere',
		when: 'Hvornår helst? (valgfrit)',
		any: 'Ligegyldigt',
		weekday: 'Hverdag',
		weekend: 'Weekend',
		send: 'Send ønske',
		/** In a recipe's Mere-menu. */
		wishForNextWeek: '♡ Ønsk til næste uge',
		/** After sending: "♡ Ønsket til uge 44". */
		wished: (week: number) => `♡ Ønsket til uge ${week}`,
		trayTitle: (week: number) => `♡ Ønsker til uge ${week}`,
		count: (count: number) => `· ${count}`,
		distribute: 'Fordel på tomme dage',
		/** After "Fordel på tomme dage". */
		distributed: (count: number) =>
			count === 0
				? 'Ingen tomme aftener passede til ønskerne.'
				: count === 1
					? '1 ønske er sat på planen'
					: `${count} ønsker er sat på planen`,
		fromLastWeek: 'Fra sidste uge',
		/** A free-text wish in the tray: ""noget med fisk"". */
		freeText: (text: string) => `"${text}"`,
		/** A favourite that has waited, as a suggestion in the tray: "♥ Sofie · 6 uger". */
		livretWaited: (name: string, weeks: number) => `♥ ${name} · ${weeks} uger`,
		livretSoon: (name: string) => `♥ ${name} · snart`,
		livretNever: (name: string) => `♥ ${name}`,
		dropHere: 'Slip her',
		/** The day picker for a wish: "Hvornår skal Pizza på planen?" */
		placeTitle: (name: string) => `Hvornår skal ${name} på planen?`,
		remove: 'Slet ønske'
	},

	/** Swapping two days of the plan (design: Nye funktioner 6 - Bytteforslag, 7a + "Byt direkte"). */
	daySwaps: {
		more: 'Mere',
		/** The day menu's heading: "Tir · Lasagne". */
		menuTitle: (weekday: string, meals: readonly string[]) =>
			meals.length ? `${weekday} · ${meals.join(', ')}` : weekday,
		swapNow: '⇄ Byt nu med…',
		propose: 'Foreslå bytte med…',
		/** The same two, when the household asks first ("Spørg først"). */
		proposeFirst: '⇄ Foreslå bytte med…',
		/** The day picker's heading: "Byt Lasagne med…" (or the weekday, for a day without meals). */
		pickTitle: (what: string) => `Byt ${what} med…`,
		noMeal: 'Ingen ret',
		message: 'Besked (valgfri)',
		messagePlaceholder: (first: string, second: string) => `Kan vi bytte ${first} og ${second}?`,
		sendProposal: 'Send forslag',
		/** On both days of a proposal: "↔ Fre". */
		tag: (weekday: string) => `↔ ${weekday}`,
		/** A meal the proposal would replace: "Aften · nu". */
		now: (label: string) => `${label} · nu`,
		becomes: 'Bliver til',
		/** Mia: "Kan vi bytte tirsdag og fredag?" */
		proposerSays: (name: string, message: string) => `${name}: "${message}"`,
		proposerAsks: (name: string) => `${name} foreslår at bytte`,
		approve: '✓ Godkend',
		reject: 'Afvis',
		/** On the proposal's other day: "Svar på forslaget under tirsdag". */
		answerUnder: (weekday: string) => `Svar på forslaget under ${weekday}`,
		waitingFor: (names: readonly string[]) => `Venter på ${orList(names)}`,
		/** "Tir ⇄ Fre · ingen ændring før det er godkendt." */
		waitingText: (first: string, second: string) =>
			`${first} ⇄ ${second} · ingen ændring før det er godkendt.`,
		withdraw: 'Træk tilbage',
		rejectedBy: (name: string) => `${name} afviste`,
		planChanged: 'Planen er ændret',
		/** What the others see on the day: "Mia byttede med fredag". */
		swappedBy: (name: string, weekday: string) => `${name} byttede med ${weekday}`,
		undo: 'Fortryd',
		/** "Byttet: tirsdag ⇄ fredag" */
		swapped: (first: string, second: string) => `Byttet: ${first} ⇄ ${second}`,
		rejected: 'Forslag afvist',
		setting: {
			title: 'Når nogen bytter dage',
			description: 'Styrer kun hvilken handling der står øverst. Begge er altid tilgængelige.',
			instant: 'Byt med det samme',
			instantNote: 'Andre kan fortryde',
			ask: 'Spørg først',
			askNote: 'Forslag skal godkendes'
		}
	},

	freezerReminders: {
		title: 'Tag op af fryseren',
		description: 'Dagen før et måltid med noget fra fryseren kommer en opgave om at tage det op.',
		label: 'Opgave om at tage op af fryseren',
		/** The example of the chore under the switch. */
		previewDish: 'Lasagne',
		previewWhen: 'til fredag · kommer torsdag',
		/** `admins` are the household's admins' names. */
		adminOnly: (admins: string[]) =>
			`🔒 Kun husstandens admin kan ændre dette. ${
				admins.length > 1 ? `${admins.slice(0, -1).join(', ')} og ${admins.at(-1)}` : admins[0]
			} er admin.`
	},

	chores: {
		title: 'Ugens opgaver',
		done: (done: number, total: number) => `${done} af ${total} opgaver klaret`,
		showFor: 'Vis opgaver for',
		freeDay: 'Fri dag',
		addForDay: '+ Opgave',
		removeChore: 'Fjern opgave',
		newChore: 'Ny opgave',
		editChore: 'Redigér opgave',
		/** A single date of a repeating chore: "Støvsug · man. 3. nov." */
		occurrenceTitle: (name: string, date: string) => `${name} · ${date}`,
		deleteChore: 'Slet opgave',
		changeAssignee: 'Skift ansvarlig',
		whatToDo: 'Hvad skal gøres?',
		chore: 'Opgave',
		who: 'Hvem',
		/** "↻ På skift": the members take turns. */
		inTurnsChoice: '↻ På skift',
		order: 'Rækkefølge',
		swapOrder: '⇄ Byt om',
		appliesTo: 'Gælder',
		thisDayOnly: 'Kun denne gang',
		thisAndFuture: 'Denne og fremtidige',
		dayOnlyHint: 'Navn og Hvem kan ændres for denne dag.',
		repeat: 'Gentag',
		repeatOptions: { never: 'Aldrig', days: 'Dag', weeks: 'Uge', months: 'Måned' },
		/** "Hver [2] uge" */
		every: 'Hver',
		unitWord: { days: 'dag', weeks: 'uge', months: 'måned' },
		interval: 'Antal',
		/** A one-off chore's date: "Dato: tor. 30. okt." */
		dateLine: (date: string) => `Dato: ${date}`,
		from: 'Fra',
		until: 'Til og med',
		addEndDate: '+ Slutdato',
		removeEndDate: 'Fjern slutdato',
		nextTimes: 'Næste gange',
		/** The date ends the sentence; "okt." brings its own full stop. */
		changeApplies: (date: string) =>
			`Ændringen gælder fra ${date}${date.endsWith('.') ? '' : '.'} Tidligere opgaver og afkrydsninger bevares.`,
		stopFrom: (date: string) => `Stop gentagelse fra ${date}`,
		skipOnce: 'Spring denne gang over',
		skip: 'Spring over',
		/** `day` is null for today, else the weekday in a sentence ("fredag"). */
		confirmSkip: (name: string, day: string | null) =>
			`Spring ${name} over ${day ?? 'i dag'}? Serien fortsætter.`,
		confirmRemove: 'Vil du fjerne opgaven?',
		confirmStop:
			'Stop gentagelsen fra den valgte dato? Tidligere opgaver og dagens udførte opgaver bevares.',
		monthlyHint: 'Mangler datoen i en måned, bruges månedens sidste dag.',
		intervalText: {
			days: (count: number) => (count === 1 ? 'Hver dag' : `Hver ${count}. dag`),
			weeks: (count: number) => (count === 1 ? 'Hver uge' : `Hver ${count}. uge`),
			months: (count: number) => (count === 1 ? 'Hver måned' : `Hver ${count}. måned`)
		},
		/** A monthly chore's date in its rule: "↻ Hver måned · den 31." */
		dayOfMonth: (day: number) => `den ${day}.`,
		/** In a rule: "↻ Hver dag · på skift". */
		inTurns: 'på skift',
		fromDate: (date: string) => `Fra ${date}`,
		untilDate: (date: string) => `Til og med ${date}`,
		stopsFrom: (date: string) => `Stopper fra ${date}`,
		/** The timeline of a series with a planned change: "Nu · Mads" → "Fra 3. nov. · Julie". */
		now: (what: string) => `Nu · ${what}`,
		changesFrom: (date: string, what: string) => `Fra ${date} · ${what}`,
		addChore: 'Tilføj opgave',
		/** A reminder the day before a meal: "Tag Lasagne op af fryseren". */
		freezerReminder: (name: string) => `Tag ${name} op af fryseren`,
		/** Under it, the meal's weekday: "Fra fryseren · til fredag". */
		freezerFrom: (weekday: string) => `Fra fryseren · til ${weekday}`
	},

	recipes: {
		title: 'Retter',
		inCollection: (count: number) => `${count} retter i jeres samling`,
		search: 'Søg i retter',
		searchPlaceholder: 'Søg i retter…',
		newRecipe: '+ Ny ret',
		empty: 'I har ingen retter endnu. Tryk på "+ Ny ret" for at oprette den første.',
		timesCooked: (count: number) => `${count}× lavet`,
		neverCooked: 'Aldrig lavet',
		ingredientCount: (count: number) => `${count} ingredienser`,
		/** The card's footer: "5 af 7 hjemme", or "Alt hjemme". */
		atHomeCount: (atHome: number, total: number) => `${atHome} af ${total} hjemme`,
		allAtHome: 'Alt hjemme',
		link: 'Link',
		untitled: 'Ny ret',
		name: 'Navn på retten',
		description: 'Beskrivelse',
		descriptionPlaceholder: 'Kort om retten og hvordan den laves',
		servings: 'Personer (opskriften)',
		image: 'Billede (URL)',
		recipeLink: 'Link til opskrift',
		recipeLinkPlaceholder: 'fx https://www.valdemarsro.dk/…',
		openLink: 'Åbn ↗',
		fetchIngredients: 'Hent ingredienser',
		fetching: 'Henter…',
		/** The import button after it filled in the ingredients: "✓ 9 hentet". */
		fetched: (count: number) => `✓ ${count} hentet`,
		fetchedNotice: (count: number) =>
			`${count === 1 ? '1 ingrediens' : `${count} ingredienser`} lagt ind nedenfor – tjek mængderne.`,
		undoFetch: 'Fortryd',
		/** "✕ Linket ser ikke rigtigt ud." */
		fetchFailed: (message: string) => `✕ ${message}`,
		supportedSites: 'Virker med valdemarsro.dk, spisbedre.dk, nemlig.com m.fl.',
		/** Under an ingredient: "⌂ Har 500 g hjemme · Fryser" when the pantry covers it. */
		haveAtHome: (quantity: string, place: string) =>
			place ? `⌂ Har ${quantity} hjemme · ${place}` : `⌂ Har ${quantity} hjemme`,
		/** "⌂ Har 100 g hjemme – mangler 200 g" when it covers some. */
		haveSome: (quantity: string, missing: string) =>
			`⌂ Har ${quantity} hjemme – mangler ${missing}`,
		tags: 'Tags',
		newTag: '+ Nyt tag',
		newTagPlaceholder: 'Nyt tag…',
		addTag: 'Tilføj',
		ingredients: 'Ingredienser',
		ingredientRows: (count: number) => `${count} stk`,
		quantity: 'Mængde',
		ingredient: 'Ingrediens',
		removeIngredient: 'Fjern ingrediens',
		addIngredient: '+ Tilføj ingrediens',
		related: 'Relaterede retter',
		relatedHint: 'Fx andre opskrifter på samme ret.',
		addRelated: 'Tilføj relateret ret…',
		/** The dashed last card of the related slider. */
		addRelatedCard: '+ Tilføj',
		removeRelated: (name: string) => `Fjern ${name} fra relaterede`,
		relatedCount: (count: number) => (count === 1 ? '⇄ 1 relateret' : `⇄ ${count} relaterede`),
		onlyOwn: 'Kun mine',
		/** The favourite ("Min livret") filter: "♥ Livretter" with Alle / Mine / "Sofies…". */
		livretFilter: '♥ Livretter',
		livretFilterAll: 'Alle',
		livretFilterMine: 'Mine',
		livretFilterOf: (name: string) => possessive(name),
		/** The chip once a filter is chosen: "♥ Mine", "♥ Sofies". */
		livretFilterChosen: (choice: string) => `♥ ${choice}`,
		addFavorite: 'Min livret',
		removeFavorite: 'Ikke min livret længere',
		/** The heart count on a recipe card: "♥ 2". */
		livretCount: (count: number) => `♥ ${count}`,
		/** Below the title: "Sofies og din livret", "Din livret", "Sofies og Jonas' livret". */
		livretOf: (others: readonly string[], mine: boolean) =>
			mine
				? others.length
					? `${andList(others.map(possessive))} og din livret`
					: 'Din livret'
				: `${andList(others.map(possessive))} livret`,
		/** "Sidst lavet for 6 uger siden", "Sidst lavet i går". */
		lastMade: (days: number, ago: string) =>
			days <= 0 ? 'Sidst lavet i dag' : days === 1 ? 'Sidst lavet i går' : `Sidst lavet for ${ago}`,
		/** Shown on a recipe the signed-in member paused ("Pause lidt"). */
		paused: 'på pause',
		relatedPrevious: 'Forrige',
		relatedNext: 'Næste',
		discardChanges: 'Kassér de ugemte ændringer?',
		deleteRecipe: 'Slet ret',
		saveRecipe: 'Gem ret'
	},

	/** "Hvad kan vi lave?" on Retter: recipes from what is in the pantry (design sheet 9). */
	suggestions: {
		views: 'Visning',
		allRecipes: 'Alle retter',
		whatCanWeMake: 'Hvad kan vi lave?',
		servings: 'Antal personer',
		/** Under the stepper: "Mængder og mangler regnes til 4". */
		countedFor: (servings: number) => `Mængder og mangler regnes til ${servings}`,
		withWhatYouHave: 'Retter med det, I har',
		/** "I har 4 af 7"; "Alt hjemme" when nothing is missing. */
		cover: (have: number, total: number) => `I har ${have} af ${total}`,
		allAtHome: 'Alt hjemme',
		/** "I har 4 af 7 · til 4 pers." */
		coverFor: (cover: string, servings: number) => `${cover} · til ${servings} pers.`,
		haveEverything: 'I har alt, der skal bruges.',
		/** "Mangler 200 g oksekød og 1 gulerod", or "Mangler 600 g kylling, 4 dl kokosmælk + 4 mere". */
		missing: (shown: readonly string[], more: number) =>
			more > 0 ? `Mangler ${shown.join(', ')} + ${more} mere` : `Mangler ${andList(shown)}`,
		assess: (names: readonly string[]) => `Mængde skal vurderes: ${names.join(', ')}`,
		/** A date chip: "1 dag tilbage", "dato overskredet". */
		daysLeft: (days: number) =>
			days < 0
				? 'dato overskredet'
				: days === 0
					? 'udløber i dag'
					: days === 1
						? '1 dag tilbage'
						: `${days} dage tilbage`,
		/** "Bruger mælk · 1 dag tilbage" */
		uses: (name: string, left: string) => `Bruger ${name} · ${left}`,
		readyFromStock: 'Klar fra lager',
		dishCount: (count: number) => (count === 1 ? '1 ret' : `${count} retter`),
		/** "8 pers. i fryseren · 3 uger" */
		dishLine: (servings: number, placeIn: string, age: string) =>
			`${servings} pers. ${placeIn} · ${age}`,
		enoughForAll: (servings: number) => `Nok til alle ${servings}`,
		enoughFor: (available: number, servings: number) =>
			`Nok til ${available} af ${servings} personer`,
		emptyPantry: 'Beholdningen er tom',
		emptyPantryHint: 'Forslag bygger på Beholdning.',
		addToPantry: 'Tilføj',
		noMatch: 'Ingen retter passer',
		noMatchHint: 'Salt og olie tæller ikke alene.',
		loadFailed: 'Forslagene kunne ikke hentes',
		loadFailedHint: 'Tjek forbindelsen, og prøv igen.',
		/** The read view (4b): it never changes the recipe. */
		back: 'Tilbage',
		readOnly: 'Kun visning',
		coverLong: (have: number, total: number) => `I har ${have} af ${total} ingredienser`,
		allAtHomeFor: (servings: number) => `Alt hjemme til ${servings} pers.`,
		status: {
			home: 'Hjemme',
			partial: 'Delvist hjemme',
			missing: 'Mangler',
			assess: 'Skal vurderes'
		},
		/** "Har 300 af 500 g" when both are in the same unit, else "Har 2 stk af 300 g". */
		haveOf: (have: string, needed: string) => `Har ${have} af ${needed}`,
		addToPlan: 'Tilføj til madplan',
		edit: '✎ Rediger',
		/** The add sheet (5a). */
		addTitle: 'Tilføj til madplan',
		addSubtitle: (name: string, servings: number) => `${name} · ${servings} pers.`,
		days: 'Dag',
		/** "Fre 23." */
		dayLabel: (weekday: string, day: number) => `${weekday} ${day}.`,
		/** "Aften: Tacos", "I dag · Aften: Tacos" */
		dayMeals: (slot: string, names: readonly string[]) => `${slot}: ${names.join(', ')}`,
		today: (text: string) => `I dag · ${text}`,
		noMeal: 'Tomt',
		meal: 'Måltid',
		shoppingNote: 'Det, I mangler, kommer på indkøbslisten.',
		/** "Tages fra fryseren ved gemning." */
		stockNote: (placeFrom: string) => `Tages ${placeFrom} ved gemning.`,
		enoughForOnly: (servings: number) => `Der er nok til ${servings} personer`,
		planFromStock: (servings: number) => `Planlæg til ${servings}`,
		planAsRecipe: (servings: number) => `Planlæg til ${servings} som almindelig ret`,
		planAsRecipeNote: 'Lageret bruges ikke. Det, I mangler, kommer på indkøbslisten.',
		/** "Tilføj fredag aften" */
		addOn: (weekday: string, slot: string) => `Tilføj ${weekday} ${slot.toLowerCase()}`,
		adding: 'Tilføjer…',
		added: 'Tilføjet',
		/** "Spaghetti bolognese · fre aften" */
		addedToast: (name: string, weekday: string, slot: string) =>
			`${name} · ${weekday.toLowerCase()} ${slot.toLowerCase()}`,
		undo: 'Fortryd'
	},

	/** "✦ Inspiration": meals, days and weeks shared between households (design sheet 10 and its prototype). */
	inspiration: {
		title: 'Inspiration',
		subtitle: 'Uger, dage og måltider delt af andre husstande',
		search: 'Søg efter ret eller ingrediens…',
		filters: { all: 'Alle', week: 'Uger', day: 'Dage', meal: 'Måltider' },
		filterLabel: 'Vis',
		kinds: { week: 'Uge', day: 'Dag', meal: 'Måltid' },
		open: { week: 'Se ugen', day: 'Se dagen', meal: 'Se måltidet' },
		people: (count: number) => `${count} pers.`,
		sharedToday: 'delt i dag',
		sharedYesterday: 'delt i går',
		/** "delt for 3 dage siden" */
		sharedAgo: (ago: string) => `delt for ${ago}`,
		usedBy: (count: number) => `Brugt af ${count}`,
		anonymous: 'Anonym',
		count: (count: number) => (count === 1 ? '1 delt madplan' : `${count} delte madplaner`),
		noMatch: 'Intet matcher. Prøv at fjerne et filter.',
		nothingYet: 'Ingen andre husstande har delt noget endnu.',
		more: 'Vis flere',
		/** "Vegetar 4/7" on a week's tag. */
		tagDays: (tag: string, days: number, of: number) => `${tag} ${days}/${of}`,
		ownRecipe: 'Jeres egen',
		atHome: (have: number, of: number) => `I har ${have} af ${of} hjemme`,
		addDay: '+ Dag',
		use: { week: 'Brug hele ugen', day: 'Brug dagen', meal: 'Brug måltidet' },
		useTitle: { week: 'Læg ugen ind i…', day: 'Læg dagen ind', meal: 'Læg måltidet ind' },
		weekLabel: 'Uge',
		dayLabel: 'Dag',
		mealLabel: 'Måltid',
		planned: 'planlagt',
		/** "Næste uge har 2 planlagte dage" */
		weekBusy: (week: string, days: number) =>
			`${week} har ${days === 1 ? '1 planlagt dag' : `${days} planlagte dage`}`,
		dayBusy: (day: string) => `${day} er allerede planlagt`,
		emptyOnly: 'Kun tomme dage',
		replaceAll: 'Erstat alt',
		alongside: 'Tilføj ved siden af',
		replace: 'Erstat',
		saveCopies: 'Gem nye retter i Retter som ◎ Kopi af …',
		servings: (from: number, to: number) =>
			`Antal personer tilpasses jeres standard (${from} → ${to}). Indkøbslisten opdateres.`,
		back: 'Tilbage',
		placeDays: (days: number) => (days === 1 ? 'Læg 1 dag ind' : `Læg ${days} dage ind`),
		/** "Læg ind fredag · aften" */
		placeOn: (day: string, slot: string | null) => `Læg ind ${day}${slot ? ` · ${slot}` : ''}`,
		/** "5 dage lagt ind i næste uge" */
		placedDays: (days: number, week: string) =>
			`${days === 1 ? '1 dag' : `${days} dage`} lagt ind i ${week}`,
		/** "Hjemmelavet pizza lagt ind fredag" */
		placedOn: (title: string, day: string) => `${title} lagt ind ${day}`,
		undo: 'Fortryd',
		previewTitle: 'Sådan ser andre den',
		/** "Dag · Familien Holm" */
		previewLabel: (kind: string, who: string) => `${kind} · ${who}`,
		titleLabel: 'Navn',
		hide: 'Skjul',
		show: 'Vis',
		showName: 'Vis vores navn',
		visible: 'Synlig i Inspiration',
		share: { week: 'Del ugen', day: 'Del dagen', meal: 'Del måltidet' },
		shareMenu: { week: '↗ Del ugen', day: '↗ Del dagen…', meal: '↗ Del måltidet…' },
		/** "Søndag med brunch" */
		dayTitle: (weekday: string, dish: string) => `${weekday} med ${dish}`,
		weekTitle: (week: number) => `Uge ${week}`,
		shared: 'Delt i Inspiration',
		ownTitle: 'Delt af jer',
		ownCount: (count: number) => `Delt af jer · ${count}`,
		sharedChip: '↗ Delt',
		everyone: 'Alle',
		linkOnly: 'Kun med link',
		/** "Uge · Alle · brugt af 31" */
		ownLine: (kind: string, who: string, usedBy: number) => `${kind} · ${who} · brugt af ${usedBy}`,
		editTitle: 'Delt i Inspiration',
		save: 'Gem',
		stopSharing: 'Stop deling',
		stopConfirm: (title: string) => `Stop deling af "${title}"? Kopier hos andre bliver.`,
		stop: 'Stop',
		/** "◎ Kopi af Familien Holm", or of Inspiration when the household shared without its name. */
		copyOf: (household: string | null) => `◎ Kopi af ${household ?? 'Inspiration'}`,
		settingsDescription:
			'Del måltider, dage og uger med andre husstande. Kopier hos andre bliver, når I stopper en deling.',
		membersMayShare: 'Må medlemmer dele til Inspiration',
		adminOnly: 'Kun husstandens admin kan dele til Inspiration.',
		/** "♡ Gemte · 3" */
		savedFilter: (count: number) => `♡ Gemte · ${count}`,
		saveShare: '♡ Gem',
		savedShare: '♥ Gem',
		saveForLater: 'Gem til senere',
		report: 'Anmeld',
		reported: 'Skjult og sendt til gennemsyn',
		copyLink: 'Kopiér link',
		linkCopied: 'Delt · linket er kopieret',
		/** Above the link when the browser wouldn't let it be copied. */
		copyFailed: 'Linket kunne ikke kopieres – kopiér det herfra',
		/** Under a share on its link page, for whoever is not signed in. */
		signInToUse: 'Log ind for at lægge den ind i jeres madplan.'
	},

	shopping: {
		title: 'Indkøbsliste',
		subtitle: 'Samlet fra ugens madplan · skaleret efter antal personer',
		/** "9 af 14", the summary card's big number, followed by `inBasketLabel`. */
		inBasket: (done: number, total: number) => `${done} af ${total}`,
		inBasketLabel: 'i kurven',
		moveToPantry: (count: number) => `Flyt ${count} til beholdning`,
		moving: 'Flytter…',
		moved: 'Flyttet',
		hideChecked: 'Skjul købte',
		/** Under the list while "Skjul købte" leaves rows out, followed by `show`. */
		hiddenCount: (count: number) =>
			count === 1 ? '1 købt vare er skjult' : `${count} købte varer er skjult`,
		show: 'Vis',
		/** Instead of the list once everything is bought and hidden, with a `showChecked` button. */
		allInBasket: 'Alt er i kurven.',
		showChecked: 'Vis købte',
		keepScreenOn: 'Hold skærmen tændt',
		screenKeptOn: 'Skærmen holdes tændt',
		resetList: 'Nulstil listen',
		/** The shopping list's add-item card. */
		addTitle: 'Tilføj vare',
		addPlaceholder: 'Tilføj vare, fx opvasketabs',
		empty: 'Ingen varer endnu – læg retter på ugens madplan, så dukker ingredienserne op her.',
		remaining: (count: number) => `${count} tilbage`,
		changePlace: 'Skift sted i beholdning',
		choosePlace: 'Vælg sted i beholdning',
		notInPantry: 'Ikke på lager',
		removeItem: 'Fjern vare',
		showParts: (name: string) => `Vis ${name} enkeltvis`,
		hideParts: (name: string) => `Skjul ${name} enkeltvis`,
		/** Under the amount to buy when it was rounded up to whole units: "4 stk" Løg. */
		need: (amount: string, unit: string) => `I skal bruge ${amount} · rundet op til hele ${unit}`,
		/** An item still to buy in units the list cannot add up, e.g. "2 stk" and "250 g" løg. */
		gatherLink: (count: number) =>
			`${count === 2 ? 'To' : count === 3 ? 'Tre' : count} enheder · Saml`,
		gatherTitle: (name: string) => `Saml ${name} til én linje`,
		/** "Chili skal bruge 2 stk og Lasagne 250 g." */
		gatherNeeds: (needs: { who: string; quantity: string }[]) =>
			needs
				.map(({ who, quantity }, i) =>
					i === 0 ? `${who} skal bruge ${quantity}` : `${who} ${quantity}`
				)
				.join(', ')
				.replace(/, ([^,]*)$/, ' og $1') + '.',
		/** "1 stk ≈ [150] g" */
		gatherAmount: (from: string, to: string) => `Hvor mange ${to} er 1 ${from}?`,
		/** "2 stk + 250 g = 550 g" */
		gatherSum: (parts: string[], total: string) => `${parts.join(' + ')} = ${total}`,
		gatherBuy: (quantity: string) => `Køb ${quantity}`,
		gatherRounded: (name: string, exact: string) => `${name} tælles i hele, så ${exact} rundes op.`,
		gatherNote: (name: string) =>
			`Gemmes på ${name} i Vareregistret og gælder for hele husstanden.`,
		gatherSave: (quantity: string) => `Saml til ${quantity}`,
		/** The button before an amount is typed. */
		gather: 'Saml',
		fromStaple: 'Basisvare',
		/** A staple that comes every N weeks, and the week it comes next by its rhythm. */
		fromStapleEvery: (weeks: number, nextWeek: number) =>
			`Basisvare · hver ${weeks}. uge · næste gang uge ${nextWeek}`,
		addedManually: 'Tilføjet manuelt',
		movedToPantry: 'Lagt på lager',
		coveredByPantry: 'Dækket af lageret',
		/** "Lageret dækker – 250 g hjemme": what is at home covers the line, not taken yet. */
		coveredByStock: (quantity: string) => `Lageret dækker – ${quantity} hjemme`,
		/** "✓ 1,5 l allerede lagt på lager": a reopened stock line. */
		alreadyMoved: (quantity: string) => `✓ ${quantity} allerede lagt på lager`,
		/** After the amount of a reopened line: "3 dl Mælk mere". */
		more: 'mere',
		/** A group with some of its lines ticked: "1 af 2 i kurven". */
		partlyInBasket: (done: number, total: number) => `${done} af ${total} i kurven`,
		/** A tick waiting in the offline outbox. */
		pending: '⟳ venter',
		/** "Fyld op til 2 l" */
		topUpTo: (quantity: string) => `Fyld op til ${quantity}`,
		skipTopUp: 'Spring opfyldning over',
		moveToNextWeek: (count: number) => `Flyt ${count} til næste uge`,
		confirmMoveToNextWeek: (count: number) =>
			count === 1
				? 'Flyt 1 vare, du ikke har købt, til næste uge?'
				: `Flyt ${count} varer, du ikke har købt, til næste uge?`,
		confirmMove: 'Flyt',
		movedToNextWeek: 'Flyttet til næste uge',
		takeFromPantry: 'Tag fra lager',
		fromPantry: (quantity: string) => `✓ ${quantity} taget fra lager`,
		putBack: 'Læg tilbage',
		checksRejected: 'Nogle afkrydsninger kunne ikke gemmes og er fjernet.',
		/** The store picker above the list; "Standard" is the household's own order of the departments. */
		store: 'Butik',
		storeDefault: 'Standard',
		editStores: 'Rediger butikker…',
		/** At the bottom of the store menu. */
		storeForHousehold: 'Valget gælder for hele husstanden.',
		/** The store menu without stores. */
		createStore: '+ Opret butik',
		createStoreHint: 'Opret en butik, så listen kan følge dens rækkefølge.',
		/** Toast after choosing a store, with "Fortryd". */
		storeChosen: (store: string) => `Listen følger nu ${store} for hele husstanden`,
		undo: 'Fortryd',
		/** Moving an item to another department (long press on a row). */
		move: 'Flyt…',
		/** Under a disabled "Flyt…" while offline. */
		needsConnection: 'Kræver netforbindelse',
		moveTitle: (name: string) => `Flyt ${name}`,
		/** "Nu: Bager i Netto · standard: Brød & bageri"; without a store or own placement only "Nu: Brød & bageri". */
		moveNow: (department: string, store: string | null, standard: string | null) =>
			`Nu: ${department}${store ? ` i ${store}` : ''}${standard ? ` · standard: ${standard}` : ''}`,
		moveWhere: 'Hvor?',
		onlyIn: (store: string) => `Kun i ${store}`,
		allStores: 'Alle butikker',
		allStoresHint: 'Gælder i alle butikker, undtagen dem, hvor varen har en særlig placering.',
		useDefault: (department: string) => `Brug standardplacering (${department})`,
		moveTo: (department: string) => `Flyt til ${department}`
	},

	pantry: {
		title: 'Beholdning',
		atHome: (count: number) => `${count} varer hjemme`,
		useSoonCount: (count: number) => `${count} skal bruges snart`,
		useSoon: 'Brug snart',
		expiresWithin: (days: number) => `Udløber inden for ${days} dage`,
		search: 'Søg i beholdning',
		searchPlaceholder: 'Søg i beholdning…',
		expiringFirst: 'Udløber først',
		oldestFirst: 'Ældste først',
		emptyPlace: 'Intet her endnu',
		usableIn: (recipes: string) => `Kan bruges i: ${recipes}`,
		addedOn: (date: string) => `Lagt ind ${date}`,
		until: (date: string) => `til ${date}`,
		editItem: 'Redigér vare',
		addItem: 'Læg på lager',
		item: 'Vare',
		dish: 'Hel ret',
		pickDish: 'Vælg ret',
		portions: 'Portioner',
		portionOf: 'à',
		perPortion: (servings: number) => `à ${servings} pers.`,
		servingsPerPortion: 'Personer pr. portion',
		namePlaceholder: 'Hvad har I? fx kyllingebryst',
		added: 'Lagt ind',
		expiresOn: 'Holdbar til',
		usedUpRemove: 'Brugt op – fjern',
		useOne: 'Brug én',
		use: 'Brug',
		usedAmount: 'Hvor meget brugte du?',
		/** "Der er 500 g tilbage bagefter." */
		leftAfter: (quantity: string) => `Der er ${quantity} tilbage bagefter.`,
		dishes: 'Færdige retter',
		/** "= Mad til 8 personer" */
		feeds: (count: number) => `= Mad til ${count} ${count === 1 ? 'person' : 'personer'}`,
		usedUp: 'Brugt op',
		scan: '▥ Scan',
		scanTitle: 'Scan stregkode',
		scanHint: 'Hold stregkoden foran kameraet.',
		scanStarting: 'Starter kameraet…',
		scanFailed:
			'Kameraet kunne ikke startes. Giv appen adgang til kameraet, eller skriv varen i stedet.',
		/** Where a scanned barcode was found; the label of the scanner's result card. */
		scanned: {
			household: 'Kendt stregkode',
			otherHousehold: 'Forslag fra en anden husstand',
			catalog: 'Fundet i Open Food Facts',
			none: 'Ukendt stregkode'
		},
		scanFound: 'Fundet – formularen er udfyldt.',
		scanUnknown: 'Skriv varen, så husker vi den til næste gang.',
		/** The result card's title for an unknown barcode. */
		newItem: 'Ny vare',
		typeInstead: 'Skriv selv i stedet',
		useScanned: 'Brug varen',
		typeScanned: 'Skriv varen',
		typeAfterFailure: 'Skriv varen i stedet',
		status: {
			expiredYesterday: 'Udløb i går',
			expiredDaysAgo: (count: number) => `Udløb for ${count} dage siden`,
			useToday: 'Brug i dag',
			useTomorrow: 'Brug i morgen',
			daysLeft: (count: number) => `${count} dage tilbage`,
			until: (date: string) => `Til ${date}`,
			monthsLeft: (count: number) => `${count} mdr. tilbage`
		}
	},

	/** Recipes, staples and register items shared between households. */
	/** One vocabulary for recipes, staples and register items: ↗ = we share, ◎ = someone else's, dashed = a copy. */
	sharing: {
		common: '◎ Fælles',
		/** "◎ Fælles · Familien Holm" */
		commonFrom: (household: string) => `◎ Fælles · ${household}`,
		shared: '↗ Delt',
		share: '↗ Del',
		/** "◎ Kopi af Lasagne" */
		copyOf: (name: string) => `◎ Kopi af ${name}`,
		stopSharing: 'Stop deling',
		sharedBanner: '↗ Delt med alle husstande',
		shareRecipeHint:
			'Andre husstande kan se og planlægge retten. Ændrer de i den, får de deres egen kopi.',
		/** "◎ Fælles · fra Familien Holm" */
		commonBanner: (household: string) => `◎ Fælles · fra ${household}`,
		commonRecipeNote:
			'En anden husstand deler denne ret. Gemmer I ændringer, får I jeres egen kopi.',
		/** "◎ Jeres kopi af Lasagne" */
		copyBanner: (name: string) => `◎ Jeres kopi af ${name}`,
		copyNote: (household: string) =>
			`I har ændret i en fælles ret. Den fælles version opdateres stadig af ${household}.`,
		copyNoteNoHousehold: 'I har ændret i en fælles ret. Den fælles version opdateres stadig.',
		useShared: 'Brug den fælles version',
		hide: 'Skjul'
	},

	consent: {
		title: 'Giv adgang',
		/** "Giv Claude adgang?" */
		titleFor: (app: string) => `Giv ${app} adgang?`,
		/** "Claude vil gerne se og ændre **beholdning, madplan, retter og opgaver** i Julies husstand." */
		asksBefore: (app: string) => `${app} vil gerne se og ændre `,
		asksScope: 'beholdning, madplan, retter og opgaver',
		asksAfter: (household: string) => ` i ${household}.`,
		returnsTo: (host: string) =>
			`Du sendes tilbage til ${host}. Du kan afbryde forbindelsen igen under Indstillinger.`,
		loopbackWarning:
			'Appen kører på din egen computer. Godkend kun, hvis du selv lige har startet forbindelsen, fx fra Claude Code.',
		approve: 'Godkend',
		deny: 'Afvis'
	},

	settings: {
		title: 'Indstillinger',
		subtitle: 'Ugeskabelon og standarder for nye uger',
		theme: {
			title: 'Farvetema',
			description: 'Gælder kun på denne enhed',
			selected: '✓ Valgt'
		},
		household: {
			description: 'Alle i husstanden deler madplan, indkøbsliste, beholdning og opgaver.',
			members: (count: number) => (count === 1 ? '1 medlem' : `${count} medlemmer`),
			invited: (count: number) => `${count} inviteret`,
			share: '↗ Del',
			inviteTitle: 'Invitér til husstanden',
			orShareCode: 'Eller del koden',
			newCode: 'Lav ny kode',
			/** After the "Lav ny kode" link. */
			newCodeHint: ' – den gamle kode holder op med at virke.',
			you: 'Dig',
			admin: 'Admin',
			confirmRemove: (name: string) => `Fjern ${name} fra husstanden?`,
			confirmRemoveYes: 'Fjern',
			invitedBadge: 'Inviteret',
			revoke: 'Tilbagekald',
			inviteByEmail: 'Invitér med email',
			invite: 'Invitér',
			inviting: 'Sender…',
			inviteSent: 'Sendt'
		},
		mealTemplate: {
			title: 'Ugeskabelon',
			description:
				'De måltider, der vises som tomme pladser med "Vælg ret", når I planlægger en uge.',
			removeSlot: (slot: string) => `Fjern ${slot}`,
			empty: 'Ingen faste måltider'
		},
		/** The household's store departments: their order on the shopping list and own ones. */
		departments: {
			title: 'Afdelinger',
			description:
				'Rækkefølgen på indkøbslisten. Hold og træk for at flytte. Tilføj egne, fx Bager.',
			remove: 'Fjern afdeling',
			confirmRemove: (name: string) => `Fjern ${name}? Dens varer kommer under Andet.`,
			edit: 'Rediger',
			editTitle: (name: string) => `Rediger ${name}`,
			save: 'Gem',
			name: 'Navn på afdelingen',
			color: 'Afdelingens farve',
			colorOption: (n: number) => `Farve ${n}`,
			newDepartment: 'Ny afdeling',
			add: '+ Tilføj'
		},
		/** Stores with their own order of the departments ("Netto"). */
		stores: {
			title: 'Butikker',
			description: 'Hver butik kan have sin egen rækkefølge og sine egne placeringer.',
			empty: 'Ingen butikker endnu. Listen følger jeres afdelinger.',
			newStore: 'Ny butik',
			newStoreExample: 'Fx Netto',
			add: '+ Tilføj',
			back: '‹ Butikker',
			name: 'Butikkens navn',
			/** A store's status in the list: "Egen rækkefølge · 1 vare ligger anderledes" or "Følger standard". */
			ownOrder: 'Egen rækkefølge',
			followsStandard: 'Følger standard',
			placedDifferently: (count: number) =>
				count === 1 ? '1 vare ligger anderledes' : `${count} varer ligger anderledes`,
			order: 'Rækkefølge · hold og træk',
			/** After a department that stands elsewhere than in the household's order: "standard: 6." */
			standardPosition: (position: number) => `standard: ${position}.`,
			placementsIn: (store: string, count: number) => `Ligger anderledes i ${store} · ${count}`,
			/** "(standard: Brød & bageri)" after an item's department in the store. */
			standardDepartment: (department: string) => `(standard: ${department})`,
			resetPlacement: 'Nulstil',
			useStandardOrder: 'Brug standardrækkefølge',
			delete: 'Slet butik',
			confirmDelete: (store: string) => `Slet ${store}?`,
			confirmDeleteLabel: 'Slet'
		},
		/** The household's meals and their order (settings and setup step 1). */
		mealSlots: {
			title: 'Jeres måltider · rækkefølge',
			moveBefore: 'Flyt før',
			moveAfter: 'Flyt efter',
			remove: 'Fjern måltid',
			newSlot: 'Eget måltid, fx Madpakke',
			add: '+ Tilføj'
		},
		choreTemplate: {
			title: 'Gentagne opgaver',
			description:
				'Opgaver med faste intervaller. Ændringer gælder fra i dag, og tidligere opgaver bevares.',
			confirmReset:
				'Stop alle gentagne opgaver fra i dag? Tidligere opgaver og dagens udførte opgaver bevares.',
			more: 'Flere handlinger',
			edit: 'Rediger',
			groups: { days: 'Dagligt', weeks: 'Ugentligt', months: 'Månedligt' },
			empty: 'Ingen faste opgaver. Tilføj fx "Tøm opvaskemaskine" hver dag.'
		},
		staples: {
			title: 'Basisvarer',
			description:
				'Kommer automatisk på indkøbslisten hver uge. Kan fjernes for en enkelt uge direkte på listen.',
			descriptionWithInterval:
				'Kommer automatisk på indkøbslisten hver uge eller med det interval, I vælger. Kan fjernes for en enkelt uge direkte på listen.',
			remove: 'Fjern basisvare',
			/** The staple's type: bought every week or kept in stock. */
			type: 'Type',
			everyWeek: 'Hver uge',
			keepInStock: '⌂ Hold på lager',
			/** When a staple every N weeks comes next by its rhythm (a week's skip is not counted). */
			nextWeek: (week: number) => `Efter rytmen: uge ${week}`,
			thisWeek: 'Efter rytmen: denne uge',
			shiftWeek: 'Skift uge i rytmen',
			/** "har 5 dl i køleskabet", after "Fyld op til 2 l". */
			inStock: (quantity: string, place: string) => `har ${quantity} ${place}`,
			keepInStockHint:
				'Hold på lager: antallet er det, I altid vil have hjemme. Indkøbslisten fylder op ud fra beholdningen og ugens madplan.',
			newStaple: 'Ny basisvare',
			newStaplePlaceholder: 'Ny basisvare, fx havregryn'
		},
		register: {
			title: 'Vareregister',
			description:
				'Én fælles liste over varer, som ingredienser, basisvarer og indkøbslisten slår op i. Afdeling, standardenhed og holdbarhed udfyldes automatisk, når du vælger en vare.',
			search: 'Søg eller opret vare',
			searchPlaceholder: 'Søg eller opret vare…',
			addToRegister: (name: string) => `Tilføj "${name}" til registret`,
			recipeCount: (count: number) => (count === 1 ? '1 ret' : `${count} retter`),
			staple: 'basisvare',
			unused: 'Ikke i brug',
			shelfLife: 'Standard holdbarhed',
			byPlace: 'Efter sted',
			deleteItem: 'Slet vare',
			empty: 'Ingen varer endnu',
			/** An item's conversions between units, so the shopping list can add "2 stk" and "300 g" up. */
			conversions: 'Omregninger',
			addConversion: '+ Omregning',
			conversionAmount: 'Mængde',
			conversionOtherAmount: 'Svarer til mængde',
			saveConversion: 'Tilføj',
			removeConversion: (conversion: string) => `Fjern ${conversion}`
		},
		servings: {
			title: 'Standard antal personer',
			description: 'Bruges når I vælger en ret til en plads'
		},
		mcp: {
			title: 'AI-assistenter',
			description:
				'Lad fx Claude se og ændre beholdning, madplan, retter og opgaver. Et token giver adgang til husstanden, indtil du tilbagekalder det.',
			empty: 'Ingen tokens endnu',
			name: 'Navn på token',
			namePlaceholder: 'Navn, fx Claude på arbejdscomputeren',
			create: 'Opret token',
			creating: 'Opretter…',
			createdDone: 'Oprettet',
			createdHint: 'Kopiér tokenet nu – det bliver ikke vist igen.',
			copyToken: 'Kopiér token',
			copyCommand: 'Kopiér kommando til Claude Code',
			created: (date: string) => `Oprettet ${date}`,
			lastUsed: (date: string) => `sidst brugt ${date}`,
			/** A connected app: "Forbundet 2. okt · brugt i dag". */
			connected: (date: string) => `Forbundet ${date}`,
			used: (date: string) => `brugt ${date}`,
			neverUsed: 'ikke brugt endnu',
			revoke: 'Tilbagekald',
			confirmRevoke: (name: string) =>
				`Tilbagekald ${name}? Assistenter med tokenet mister adgangen.`,
			confirmRevokeYes: 'Tilbagekald',
			connectedApps: 'Forbundne apps',
			noConnectedApps: 'Ingen forbundne apps',
			noConnectedAppsHint: 'Tilføj Daglio som connector i Claude for at forbinde.',
			tokens: 'Personlige tokens',
			disconnect: 'Afbryd',
			confirmDisconnect: (name: string) => `Afbryd ${name}? Appen mister adgangen med det samme.`,
			confirmDisconnectYes: 'Afbryd'
		}
	},

	/** Platform super-admin page: every household on the platform. */
	admin: {
		title: 'Admin',
		subtitle: 'Alle husstande på platformen · kun synlig for administratorer',
		stats: {
			users: 'Brugere',
			invited: (count: number) => `${count} inviteret`,
			noneWaiting: 'Ingen ventende',
			active7: 'Aktive · 7 dage',
			activeShare: (percent: number) => `${percent}% af brugerne`,
			households: 'Husstande',
			perHousehold: (average: string) => `${average} pr. husstand`,
			recipes: 'Opskrifter',
			acrossHouseholds: 'På tværs af husstande',
			items: 'Varer',
			inAllRegisters: 'I alle vareregistre'
		},
		tabs: {
			users: 'Brugere',
			recipes: 'Opskrifter',
			items: 'Varer',
			households: 'Husstande',
			units: 'Enheder',
			reports: 'Anmeldelser',
			stats: 'Statistik'
		},
		/** Shares households reported in Inspiration. */
		reports: {
			intro:
				'Delinger i Inspiration, som husstande har anmeldt. De er skjult for dem, der anmeldte.',
			loading: 'Henter anmeldelser…',
			none: 'Ingen anmeldelser lige nu.',
			/** "Uge · Familien Holm · 2 anmeldelser" */
			line: (kind: string, household: string, reports: number) =>
				`${kind} · ${household} · ${reports === 1 ? '1 anmeldelse' : `${reports} anmeldelser`}`,
			open: 'Åbn',
			hide: 'Skjul for alle',
			hideConfirm: (title: string) => `Skjul "${title}" for alle? Delingen stoppes.`,
			keep: 'Behold',
			hidden: 'Skjult for alle',
			kept: 'Beholdt'
		},
		units: {
			intro:
				'Enhederne, alle husstande vælger imellem. En enhed, der er i brug, kan skjules, men hvad dens mængder betyder, ligger fast.',
			loading: 'Henter enheder…',
			active: 'Aktiv',
			/** Counted in whole pieces. */
			countable: 'Hele',
			inUse: 'I brug',
			system: 'Bruges af appen',
			systemHint: 'Bruges af appen: kan kun flyttes og få andre navne.',
			/** A unit others are the same measure as. */
			baseUnit: 'Grundenhed',
			conversion: 'Omregning',
			none: 'Ingen',
			base: 'Samme mål',
			fixed: 'Fast',
			/** Before the factor and target: "1 kg =" [1000] [g]. */
			oneIs: (code: string) => `1 ${code} =`,
			factor: 'Antal',
			target: 'Omregnes til',
			lockedHint: 'Enheden er i brug, så omregningen ligger fast.',
			aliases: (code: string) => `Andre navne for ${code}`,
			aliasesPlaceholder: 'Andre navne, fx kilo, kilogram',
			moveUp: (code: string) => `Flyt ${code} op`,
			moveDown: (code: string) => `Flyt ${code} ned`,
			remove: (code: string) => `Slet ${code}`,
			confirmRemove: (code: string) => `Slet ${code}?`,
			newUnit: 'Ny enhed',
			add: 'Tilføj',
			save: 'Gem enheder'
		},
		statistics: {
			loading: 'Henter statistik…',
			period: (weeks: number) => `Ugetal og lister dækker de seneste ${weeks} uger`,
			sections: {
				activity: 'Aktivitet og vækst',
				mealPlan: 'Madplan og opskrifter',
				pantry: 'Spisekammer og madspild',
				other: 'Pligter, integrationer og datakvalitet'
			},
			share: (percent: number, of: string) => `${percent}% af ${of}`,
			ofUsers: 'brugerne',
			ofHouseholds: 'husstandene',
			ofRecipes: 'opskrifterne',
			weeklyAverage: (average: string) => `Snit ${average} pr. uge`,
			lastWeeks: (weeks: number) => `Seneste ${weeks} uger`,
			households: (count: number) => (count === 1 ? '1 husstand' : `${count} husstande`),
			activeToday: 'Aktive · 24 timer',
			active30: 'Aktive · 30 dage',
			quietHouseholds: 'Stille husstande',
			quietSub: 'Ingen aktivitet i 30 dage',
			pendingInvites: 'Ventende invitationer',
			oldestInvite: (ago: string) => `Ældste: ${ago.toLocaleLowerCase('da')}`,
			noInvites: 'Ingen ventende',
			planningThisWeek: 'Planlægger denne uge',
			mealsThisWeek: 'Måltider denne uge',
			fromPantry: 'Spist fra lageret',
			imported: 'Importerede',
			shared: 'Delte opskrifter',
			plannedByOthers: (count: number) => `Planlagt ${count} gange af andre`,
			copies: 'Tilrettede kopier',
			copiesSub: 'Egne kopier af delte opskrifter',
			relations: 'Relationer',
			relationsSub: 'Par af relaterede retter',
			neverPlanned: 'Aldrig planlagt',
			pantryItems: 'Varer på lager',
			acrossHouseholds: 'På tværs af husstande',
			expired: 'Udløbet',
			expiredSub: 'Over datoen',
			expiringSoon: 'Udløber snart',
			expiringSub: 'Inden for 3 dage',
			barcodes: 'Stregkoder',
			productCache: 'Open Food Facts',
			unknownProducts: (count: number) => `${count} ukendte`,
			choresDone: 'Pligter udført · uge',
			choresSkipped: 'Sprunget over · uge',
			apiTokens: 'API-nøgler',
			usedLast30: (count: number) => `${count} brugt · 30 dage`,
			duplicates: 'Mulige dubletter',
			inRegisters: 'I vareregistrene',
			aliases: 'Flettede navne',
			aliasesSub: 'Rettes automatisk',
			withoutIngredients: 'Uden ingredienser',
			withoutImage: 'Uden billede',
			ofRecipesCount: (count: number) => `Af ${count} opskrifter`,
			itemsInOther: 'Varer i "Andet"',
			itemsInOtherSub: 'Mangler en afdeling',
			charts: {
				newUsers: 'Nye brugere pr. uge',
				newHouseholds: 'Nye husstande pr. uge',
				plannedMeals: 'Planlagte måltider pr. uge',
				newRecipes: 'Nye opskrifter pr. uge',
				movedToPantry: 'Flyttet fra indkøb til lager pr. uge',
				choresDone: 'Udførte pligter pr. uge',
				choresSkipped: 'Pligter sprunget over pr. uge'
			},
			/** "Uge 39: 14" for one bar of a weekly chart. */
			weekValue: (week: number, count: number) => `Uge ${week}: ${count}`,
			weekShort: (week: number) => `Uge ${week}`,
			/** The last bar's label: "Uge 40 · i gang". */
			weekInProgress: (week: number) => `Uge ${week} · i gang`,
			thisWeekNote: 'Den stiplede søjle er ugen, der er i gang',
			/** The section chips' group label. */
			sectionPicker: 'Vælg område',
			lists: {
				quietHouseholds: 'Stille husstande',
				topRecipes: 'Mest planlagte retter',
				importSites: 'Importeret fra',
				places: 'Varer pr. sted',
				oauthClients: 'MCP-forbindelser',
				features: 'Funktioner slået til'
			},
			/** Households' own storage places counted together. */
			ownPlaces: 'Egne steder',
			quietHousehold: (members: number, active: string) =>
				`${members === 1 ? '1 medlem' : `${members} medlemmer`} · Aktiv: ${active.toLocaleLowerCase('da')}`,
			lastUsed: (when: string) => `Sidst brugt: ${when.toLocaleLowerCase('da')}`,
			none: 'Intet endnu'
		},
		placeholders: {
			users: 'Søg navn, email eller husstand…',
			recipes: 'Søg ret eller ingrediens…',
			items: 'Søg vare…',
			households: 'Søg husstand…'
		},
		household: 'Husstand',
		allHouseholds: 'Alle husstande',
		householdOption: (name: string, active: number) => `${name} (${active})`,
		back: 'Alle husstande',
		active: (when: string) => `Aktiv: ${when}`,
		never: 'Aldrig',
		householdAdmin: 'Husstandsadmin',
		/** The facts of an opened user row. */
		facts: {
			household: 'Husstand',
			role: 'Rolle',
			active: 'Aktiv',
			created: 'Oprettet'
		},
		roleMember: 'Medlem',
		roleAdmin: 'Admin',
		/** "I dag · 3 enheder" */
		activeOn: (when: string, devices: number) =>
			`${when} · ${devices === 1 ? '1 enhed' : `${devices} enheder`}`,
		dangerZone: 'Farezone',
		showUser: (name: string) => `Vis ${name}`,
		hideUser: (name: string) => `Skjul ${name}`,
		householdRecipes: 'Husstandens retter',
		resendInvite: 'Gensend invitation',
		inviteResent: (email: string) => `Invitationen er sendt igen til ${email}`,
		revokeInvite: 'Tilbagekald',
		noHousehold: 'Ingen husstand',
		makeAdmin: 'Gør til admin',
		revokeAdmin: 'Fjern som admin',
		signOut: 'Log ud overalt',
		signedOut: (name: string) => `${name} er logget ud på alle enheder`,
		/** The households tab: "2 medlemmer + 1 inviteret · 14 retter" */
		householdMeta: (members: number, invited: number, recipes: number) =>
			`${members === 1 ? '1 medlem' : `${members} medlemmer`}${invited ? ` + ${invited} inviteret` : ''} · ${recipes === 1 ? '1 ret' : `${recipes} retter`}`,
		deleteHouseholdShort: 'Slet…',
		removeFromHousehold: 'Fjern fra husstand',
		confirmRemoveMember: (name: string, household: string) => `Fjern ${name} fra ${household}?`,
		confirmRemove: 'Fjern',
		deleteUser: 'Slet bruger',
		confirmDeleteUser: (name: string) => `Slet ${name} permanent?`,
		confirmDeleteLastUser: (name: string, household: string) =>
			`Slet ${name} og husstanden ${household} permanent?`,
		deleteHousehold: 'Slet husstand',
		confirmDeleteHousehold: (household: string, members: number) =>
			`Slet ${household} og ${members === 1 ? '1 brugerkonto' : `${members} brugerkonti`} permanent?`,
		recipeMeta: (household: string, ingredients: number, servings: number) =>
			`${household} · ${ingredients} ${ingredients === 1 ? 'ingrediens' : 'ingredienser'} · ${servings} pers.`,
		relatedTo: (names: string) => `Relateret: ${names}`,
		deleteRecipe: 'Slet ret',
		confirmDeleteRecipe: 'Slet retten?',
		confirmDelete: 'Slet',
		itemName: 'Varenavn',
		defaultUnit: 'Standardenhed',
		department: 'Afdeling',
		itemUsage: (household: string, recipes: number) =>
			`${household} · ${recipes === 0 ? 'ikke i brug' : recipes === 1 ? '1 ret' : `${recipes} retter`}`,
		deleteItem: 'Slet vare',
		deleteUsedItem: (recipes: number) => `Bruges i ${recipes} ret(ter) – fjernes kun fra registret`,
		noMatches: 'Intet matcher søgningen.',
		selectItem: (name: string, household: string) => `Vælg ${name} (${household})`,
		mergeSelected: (count: number) => (count === 1 ? 'Flet 1 vare' : `Flet ${count} varer`),
		/** The selection bar: "3 valgt". */
		selectedCount: (count: number) => `${count} valgt`,
		clearSelection: 'Ryd valg',
		mergeTitle: 'Flet varer',
		mergeSubtitle:
			'Navnet rettes i alle husstande: vareregister, retter, basisvarer, lager og indkøbslister.',
		mergeInto: 'Flet til',
		otherName: 'Andet navn…',
		nameUsage: (households: number) =>
			households === 1 ? '1 husstand' : `${households} husstande`,
		/** How many recipes use a name, in the merge dialog. */
		recipeUsage: (recipes: number) => (recipes === 1 ? '1 ret' : `${recipes} retter`),
		mergeImpact: (households: number, recipes: number) =>
			`Berører ${households === 1 ? '1 husstand' : `${households} husstande`} og ${recipes === 1 ? '1 ret' : `${recipes} retter`}.`,
		mergeAliasNote: 'Skriver nogen de andre stavemåder igen, rettes de automatisk.',
		confirmMerge: 'Flet',
		merging: 'Fletter…',
		merged: 'Flettet',
		duplicates: (count: number) => `Mulige dubletter (${count})`,
		duplicateMerge: 'Flet…',
		notDuplicates: 'Ikke dubletter',
		editorBadge: (household: string) => `Admin · ${household}`,
		/** Features switched on per household; one label per `HouseholdFeature`. */
		features: {
			barcodeScanning: 'Stregkodescanning',
			daySwap: 'Bytte dage',
			mealWishes: 'Ønsker',
			mealSuggestions: 'Hvad kan vi lave?',
			freezerReminders: 'Tag op af fryseren',
			stapleIntervals: 'Basisvarer med interval',
			storeLayouts: 'Butikker',
			inspiration: 'Inspiration'
		},
		featureToggle: (feature: string, household: string) => `${feature} for ${household}`
	}
};

export type Messages = typeof da;
